/**
 * Client side of the support-portal remote-debug queue. The server's emr_debug_commands
 * collection is the source of truth; this executor pulls pending commands for THIS device
 * (stable emrClientDeviceId) and executes them:
 *
 *  - IDB_REPORT             per-store record counts (native count(), nothing decrypted),
 *                           DB version, per-store last-sync timestamps, storage estimate,
 *                           roles, current check-in, app version
 *  - FORCE_FULL_RESYNC      startEmrBackgroundSync({ force }) over the known brands
 *  - WIPE_AND_RESYNC        delete the erm-database + clear sync timestamps, then resync
 *  - CLEAR_SYNC_TIMESTAMPS  clear the per-store last-sync markers only
 *
 * Triggers: app load, window focus (throttled), the periodic tick, and the Agora RTM
 * wake-up ("emr-debug-commands-pending") for real-time checks. One tiny GET per trigger —
 * the scheduled path adds essentially no traffic. Results are posted once per device;
 * the server ignores duplicates.
 */

import { getCPDataWithoutParams, postCPDataWithParams } from 'src/clinic/services'
import {
  IDB_STORES,
  LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED,
} from 'src/consumer/constants'
import { getClientDeviceInfo } from 'src/consumer/services/deviceInfoCollector'
import { deleteERMDatabase, ERM_DB_VERSION } from 'src/emr/database/indexedDb'
import { clearDecryptedStoreCache, countRecordsInStore } from 'src/emr/database/indexedDbDao'
import {
  getKnownEmrSyncBrandIds,
  startEmrBackgroundSync,
} from 'src/emr/database/emrBackgroundSync'
import { buildEmrDeviceLabel, getOrCreateEmrDeviceId } from './emrDeviceIdentity'

const PENDING_PATH = '/api/secure/clinic-practitioner/emr-debug/commands/pending'
const RESULT_PATH = (commandId) =>
  `/api/secure/clinic-practitioner/emr-debug/commands/${commandId}/result`

let inFlightCheck = null

function readJsonFromLocalStorage(key) {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch (error) {
    return null
  }
}

// ------------------------------------------------------------------ executors

async function buildIdbReport() {
  const storeCounts = {}
  for (const storeConfig of Object.values(IDB_STORES)) {
    try {
      storeCounts[storeConfig.name] = await countRecordsInStore(storeConfig.name)
    } catch (error) {
      storeCounts[storeConfig.name] = `error: ${error?.message}`
    }
  }

  const lastSyncedAt = {}
  Object.entries(LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED).forEach(([storeKey, storageKey]) => {
    lastSyncedAt[storeKey] = localStorage.getItem(storageKey) || null
  })

  let storage = null
  try {
    if (navigator.storage?.estimate) {
      const estimate = await navigator.storage.estimate()
      storage = { usageBytes: estimate.usage, quotaBytes: estimate.quota }
    }
  } catch (error) {
    storage = { error: error?.message }
  }

  const checkedInDetails = readJsonFromLocalStorage('checkedInDetails')
  const authorities = readJsonFromLocalStorage('authorities')

  return {
    dbVersion: ERM_DB_VERSION,
    storeCounts,
    lastSyncedAt,
    storage,
    knownSyncBrandIds: getKnownEmrSyncBrandIds(),
    authorities: Array.isArray(authorities) ? authorities : null,
    checkedIn: checkedInDetails
      ? {
          clinicBrandId: checkedInDetails.clinicBrandId,
          clinicLocationId: checkedInDetails.clinicLocationId,
          chamberRoomId: checkedInDetails.chamberRoomId,
          lastValidated: checkedInDetails.lastValidated,
        }
      : null,
    onLine: typeof navigator !== 'undefined' ? navigator.onLine : null,
    reportedAt: new Date().toISOString(),
  }
}

function summarizeSyncRun(runResult) {
  return {
    synced: runResult?.synced,
    skipped: runResult?.skipped,
    failures: runResult?.failures,
    durationMs: runResult?.durationMs,
    skippedRun: runResult?.skippedRun,
    failedRun: runResult?.failedRun,
  }
}

function clearSyncTimestamps() {
  const cleared = []
  Object.values(LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED).forEach((storageKey) => {
    if (localStorage.getItem(storageKey) !== null) {
      localStorage.removeItem(storageKey)
      cleared.push(storageKey)
    }
  })
  return cleared
}

async function forceFullResync() {
  const brandIds = getKnownEmrSyncBrandIds()
  if (brandIds.length === 0) {
    return { error: 'no known clinic brands on this client yet — open any EMR page first' }
  }
  const runResult = await startEmrBackgroundSync({
    clinicBrandIds: brandIds,
    force: true,
    waitForIdleFirst: false,
  })
  return { brandIds, run: summarizeSyncRun(runResult) }
}

async function wipeAndResync() {
  const clearedTimestamps = clearSyncTimestamps()
  clearDecryptedStoreCache()
  let wiped = false
  try {
    // bounded: deletion blocks while other tabs hold connections — don't hang the command
    await Promise.race([
      deleteERMDatabase().then(() => {
        wiped = true
      }),
      new Promise((resolve) => setTimeout(resolve, 8000)),
    ])
  } catch (error) {
    return { wiped: false, error: `deleteDatabase failed: ${error?.message}`, clearedTimestamps }
  }
  const resync = await forceFullResync()
  return { wiped, clearedTimestamps, ...resync }
}

async function executeCommand(command) {
  switch (command?.commandType) {
    case 'IDB_REPORT':
      return await buildIdbReport()
    case 'FORCE_FULL_RESYNC':
      return await forceFullResync()
    case 'WIPE_AND_RESYNC':
      return await wipeAndResync()
    case 'CLEAR_SYNC_TIMESTAMPS':
      return { clearedTimestamps: clearSyncTimestamps() }
    default:
      throw new Error(`Unknown commandType: ${command?.commandType}`)
  }
}

// ------------------------------------------------------------------ transport

async function postResult(commandId, result) {
  await postCPDataWithParams(RESULT_PATH(commandId), result, {})
}

/**
 * Fetch + execute pending commands for this device. Safe to call from anywhere; runs are
 * single-flight and errors never propagate to the caller.
 */
export async function checkAndExecutePendingDebugCommands(deliveredVia = 'poll') {
  if (inFlightCheck) {
    return inFlightCheck
  }
  inFlightCheck = (async () => {
    try {
      const deviceId = getOrCreateEmrDeviceId()
      if (!deviceId) {
        return
      }
      const response = await getCPDataWithoutParams(
        `${PENDING_PATH}?deviceId=${encodeURIComponent(deviceId)}`,
      )
      const pending = response?.data?.data?.emrDebugCommands || response?.data?.emrDebugCommands
      if (!Array.isArray(pending) || pending.length === 0) {
        return
      }
      const deviceInfo = getClientDeviceInfo()
      for (const command of pending) {
        const base = {
          deviceId,
          deviceLabel: buildEmrDeviceLabel(deviceInfo),
          deliveredVia,
          appVersion: deviceInfo?.appVersion,
          pageUrl: window.location?.pathname,
        }
        try {
          const report = await executeCommand(command)
          await postResult(command.id, { ...base, status: 'ok', report })
        } catch (error) {
          try {
            await postResult(command.id, { ...base, status: 'error', error: error?.message })
          } catch (postError) {
            console.warn('Could not report EMR debug command failure', postError)
          }
        }
      }
    } catch (error) {
      // a failed check just waits for the next trigger — never surface to the user
      console.warn('EMR debug command check failed', error)
    } finally {
      inFlightCheck = null
    }
  })()
  return inFlightCheck
}
